import React, { useState } from "react";
import { Formik, Form } from "formik";
import FormField from "components/FormField";
import Button from "components/Button";
import {
  intakeCategories,
  outcomeCategories,
  getUniqueTypeSubtypePairs,
} from "./utils";
import { apiFetch, formatUiErrors } from "utils";
import { toast } from "react-toastify";
import { useAuthState, useAuthDispatch } from "context";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Error from "components/Error";
import { FORM_MODES } from "utils";
import { useParams } from "react-router-dom";

export default function ShelterBuddyApiMappingTable({ formMode, setFormMode }) {
  const { token } = useAuthState();
  const { orgId } = useParams();
  const dispatch = useAuthDispatch();
  const queryClient = useQueryClient();
  const [sacCategoryGroups, setSacCategoryGroups] = useState([]);
  const [sacCategoryData, setSacCategoryData] = useState([]);

  const {
    isFetching,
    error,
    data: queryData,
  } = useQuery(
    ["organizationShelterBuddyMappings", { orgId }],
    () =>
      apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/shelterbuddy_mappings?filter[org_id][eq]=${orgId}`,
        includeMeta: true,
      }),
    {
      onSuccess: (data) => {
        setSacCategoryGroups(data.data);
        setSacCategoryData(getUniqueTypeSubtypePairs(data.data));
      },
    }
  );

  const resolvedGroups =
    sacCategoryGroups.length > 0 ? sacCategoryGroups : queryData?.data ?? [];

  const resolvedSacCategoryData =
    sacCategoryData.length > 0
      ? sacCategoryData
      : getUniqueTypeSubtypePairs(resolvedGroups);

  const outcomeData = resolvedSacCategoryData.filter(
    (obj) => obj?.event_category === "Outcome"
  );
  const intakeData = resolvedSacCategoryData.filter(
    (obj) => obj?.event_category === "Intake"
  );

  const handleSave = (group) => {
    const oldGroupIndex = resolvedSacCategoryData.findIndex(
      (obj) => obj.key === group.key
    );
    const newGroup = resolvedSacCategoryData.filter(
      (obj) => obj.key !== group.key
    );
    newGroup.splice(oldGroupIndex, 0, group);
    setSacCategoryData([...newGroup]);
  };

  const { mutate: mapCategories, isLoading } = useMutation(
    () =>
      apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/v1/shelterbuddy_mappings",
        body: {
          orgId,
          sacCategoryData: resolvedSacCategoryData,
        },
      }),
    {
      onSuccess: () => {
        queryClient.setQueryData(
          ["organizationShelterBuddyMappings", { orgId }],
          (old) => ({
            ...old,
            data: old.data.map((item) => {
              const mapped = resolvedSacCategoryData.find(
                (s) =>
                  s.eventType === (item.eventType ?? item.event_type) &&
                  (s.eventSubtype ===
                    (item.eventSubtype ?? item.event_subtype) ||
                    (s.eventSubtype === null && item.event_subtype === null))
              );
              return mapped
                ? {
                    ...item,
                    sacCategory:
                      mapped.sacCategory === "null" ? null : mapped.sacCategory,
                  }
                : item;
            }),
          })
        );
        queryClient.invalidateQueries([
          "organizationShelterBuddyMappings",
          { orgId },
        ]);
        setSacCategoryGroups([]);
        setSacCategoryData([]);
        toast.success("SAC Categories mapped successfully", {
          autoClose: 3000,
        });
        setFormMode("VIEW");
      },
      onError: (error) => {
        toast.error(() => formatUiErrors(error), {
          autoClose: 5000,
          className: "toast-center",
        });
      },
    }
  );

  if (error) return <Error>{formatUiErrors(error)}</Error>;

  return (
    <>
      <div className="flex justify-end gap-4 my-4">
        {formMode !== FORM_MODES.VIEW && (
          <div className="flex justify-end space-x-4">
            <Button
              emphasis="transparent"
              onClick={() => {
                setFormMode("VIEW");
                setSacCategoryData(getUniqueTypeSubtypePairs(resolvedGroups));
              }}
            >
              Cancel
            </Button>
          </div>
        )}
        {formMode === FORM_MODES.VIEW && (
          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => {
                setFormMode("EDIT");
                setSacCategoryData(getUniqueTypeSubtypePairs(resolvedGroups));
              }}
            >
              Edit
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="grid lg:grid-cols-3 w-full items-center">
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            Intakes
          </span>
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            Subtypes
          </span>
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            SAC Category
          </span>
        </div>
        <div>
          {isFetching && intakeData.length === 0 ? (
            <div className="flex w-full justify-center">
              <span className="px-4 py-4 italic text-table">
                Loading data&hellip;
              </span>
            </div>
          ) : (
            intakeData.length === 0 && (
              <div className="flex w-full justify-center">
                <span className="px-4 py-4 italic text-table">
                  No data found
                </span>
              </div>
            )
          )}
          {intakeData.map((obj, idx) => (
            <CategoryForm
              formMode={formMode}
              key={`${idx}_key`}
              data={obj}
              categoryData={intakeCategories}
              idx={idx}
              handleSave={handleSave}
            />
          ))}
        </div>
      </div>

      <div>
        <div className="grid lg:grid-cols-3 w-full items-center">
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            Outcomes
          </span>
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            Subtypes
          </span>
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            SAC Category
          </span>
        </div>
        <div>
          {isFetching ? (
            <div className="flex w-full justify-center">
              <span className="px-4 py-4 italic text-table">
                Loading data&hellip;
              </span>
            </div>
          ) : (
            outcomeData.length === 0 && (
              <div className="flex w-full justify-center">
                <span className="px-4 py-4 italic text-table">
                  No data found
                </span>
              </div>
            )
          )}
          {outcomeData.map((obj, idx) => (
            <CategoryForm
              formMode={formMode}
              key={`${idx}_key`}
              data={obj}
              categoryData={outcomeCategories}
              idx={idx}
              handleSave={handleSave}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-4 my-4">
        {formMode !== FORM_MODES.VIEW && (
          <Button
            disabled={isLoading}
            isLoading={isLoading}
            loadingText={<>Mapping&hellip;</>}
            onClick={mapCategories}
            type="button"
          >
            Save
          </Button>
        )}
        {formMode !== FORM_MODES.VIEW && (
          <Button
            emphasis="transparent"
            onClick={() => {
              setFormMode("VIEW");
              setSacCategoryData(getUniqueTypeSubtypePairs(resolvedGroups));
            }}
          >
            Cancel
          </Button>
        )}
        {formMode === FORM_MODES.VIEW && (
          <Button
            onClick={() => {
              setFormMode("EDIT");
              setSacCategoryData(getUniqueTypeSubtypePairs(resolvedGroups));
            }}
          >
            Edit
          </Button>
        )}
      </div>
    </>
  );
}

const CategoryForm = ({ data, categoryData, idx, handleSave, formMode }) => (
  <Formik initialValues={data} enableReinitialize>
    {({ values }) => (
      <Form
        className={`${
          idx % 2 === 0 ? "" : "bg-gray-100"
        } pr-4 py-2 border-b grid lg:grid-cols-3 w-full items-center`}
      >
        <span className="mx-4 py-2 bg-transparent">{values.eventType}</span>
        <span className="mx-4 py-2 bg-transparent">{values.eventSubtype}</span>
        {formMode === FORM_MODES.VIEW ? (
          <span className="mx-4 py-2 bg-transparent">
            {categoryData.find((c) => c.value === values.sacCategory)?.name ??
              "--"}
          </span>
        ) : (
          <FormField
            name="sacCategory"
            label=""
            as="select"
            onChange={(e) => {
              handleSave({ ...values, sacCategory: e.target.value });
            }}
          >
            <option value={"null"} disabled>
              -- Choose --
            </option>
            {categoryData.map((category) => (
              <option value={category.value} key={category.value}>
                {category.name}
              </option>
            ))}
          </FormField>
        )}
      </Form>
    )}
  </Formik>
);
